import { FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { useEffect, useMemo, useRef, useState } from 'react'
import { BaseFieldProps, CascadingOption, CascadingCardOption, isCascadingCardOptionArray } from '../types'
import { cn } from '@/lib/utils'
import { SingleLevelCombobox } from './SingleLevelCombobox'
import { MultiLevelCombobox } from './MultiLevelCombobox'
import { DynamicIcon } from '@/components/icons/DynamicIcon'
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/shadcn/card'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { IconInformationOutline } from '@icons/IconInformationOutline'
import { DocumentationButton } from '../DocumentationButton'

interface CascadingFieldValue {
  [key: string]: string | string[] | unknown
}

// Helper function to infer levels from options structure
function inferLevelsFromOptions(options: (CascadingOption | CascadingCardOption)[] | undefined): string[] {
  // Compute the maximum depth anywhere in the tree (cap at 5)
  const getMaxDepth = (opts: (CascadingOption | CascadingCardOption)[] | undefined, currentDepth: number): number => {
    if (!Array.isArray(opts) || opts.length === 0) {
      return currentDepth
    }
    let maxDepth = currentDepth
    for (const opt of opts) {
      const children = (opt as CascadingOption).options
      if (Array.isArray(children) && children.length > 0) {
        maxDepth = Math.max(maxDepth, getMaxDepth(children, currentDepth + 1))
      }
    }
    return maxDepth
  }

  const depth = Math.min(getMaxDepth(options, 1), 5)
  return Array.from({ length: depth }, (_, i) => `level${i + 1}`)
}

// CardSelector for first level card selection
interface CardSelectorProps {
  options: CascadingCardOption[]
  value: string
  onChange: (value: string) => void
  disabled?: boolean
}

const CardSelector: React.FC<CardSelectorProps> = ({ options, value, onChange, disabled }) => (
  <div className="flex flex-col gap-2">
    <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-3 py-3">
      {options.map((option) => {
        const isSelected = value === option.value
        return (
          <div key={option.value}>
            <Card
              className={cn(
                'flex h-full w-full cursor-pointer flex-col justify-between overflow-hidden py-0 transition-all',
                isSelected ? 'border-primary bg-primary/10 ring-2 ring-primary' : 'border-border bg-muted',
                disabled && 'pointer-events-none opacity-50'
              )}
              onClick={() => {
                if (disabled) return
                if (isSelected) {
                  onChange('') // Deselect if already selected
                } else {
                  onChange(option.value)
                }
              }}
              tabIndex={0}
              role="button"
              aria-pressed={isSelected}
            >
              <CardHeader className="bg-muted py-5 text-foreground">
                <CardTitle className="max-w-full truncate text-center text-base font-light">
                  <TooltipButton tooltip={option.label}>
                    <span className="truncate">{option.label}</span>
                  </TooltipButton>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="center mb-2 flex w-full items-center justify-center gap-5">
                  <DynamicIcon
                    style={{ color: option.card.primaryIcon.colour || 'hsl(var(--primary))' }}
                    iconName={option.card.primaryIcon.icon}
                    size={40}
                  />
                  {option.card.secondaryIcon && (
                    <DynamicIcon
                      style={{ color: option.card.secondaryIcon.colour || 'hsl(var(--accent))' }}
                      iconName={option.card.secondaryIcon.icon}
                      size={40}
                    />
                  )}
                </div>
              </CardContent>
              <CardFooter className="mb-0 mt-auto h-24 justify-center bg-muted py-5">
                <span className="line-clamp-3 leading-snug text-muted-foreground">{option.card.description}</span>
              </CardFooter>
            </Card>
          </div>
        )
      })}
    </div>
  </div>
)

export const CascadingField = ({ field, formFieldName, control, variant }: BaseFieldProps) => {
  // Infer levels from the options structure
  const levels = useMemo(() => inferLevelsFromOptions(field.options), [field.options])

  // Get the field labels from the schema or use defaults
  const fieldLabels = useMemo(() => field.fieldLabels || {}, [field.fieldLabels])

  // Track options for each level
  const [levelOptions, setLevelOptions] = useState<Record<string, CascadingOption[]>>({})

  // Store the current form value for use in effects
  const [currentValue, setCurrentValue] = useState<CascadingFieldValue>({})
  const isMountedRef = useRef(false)

  // Set mounted flag
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Initialize first level options
  useEffect(() => {
    if (levels.length > 0) {
      const firstLevelKey = levels[0]
      const newOptions = Array.isArray(field.options) ? field.options : []

      setLevelOptions((prev) => {
        // Only update if options have changed
        const currentOptions = prev[firstLevelKey]

        if (JSON.stringify(currentOptions) === JSON.stringify(newOptions)) {
          return prev
        }

        return {
          ...prev,
          [firstLevelKey]: newOptions,
        }
      })
    }
  }, [field.options, levels])

  // Update options for each level when parent level selection changes
  useEffect(() => {
    // Skip the first level as it's already initialized
    for (let i = 1; i < levels.length; i++) {
      const parentLevel = levels[i - 1]
      const currentLevel = levels[i]

      if (currentValue[parentLevel]) {
        const parentOptions = levelOptions[parentLevel] || []
        const selectedParent = parentOptions.find((option) => option.value === currentValue[parentLevel])

        if (selectedParent?.options) {
          const newOptions = Array.isArray(selectedParent.options) ? selectedParent.options : []

          setLevelOptions((prevOptions) => {
            // Only update if options have changed
            if (JSON.stringify(prevOptions[currentLevel]) === JSON.stringify(newOptions)) {
              return prevOptions
            }

            return {
              ...prevOptions,
              [currentLevel]: newOptions,
            }
          })
        } else if (levelOptions[currentLevel]?.length > 0) {
          // Clear child options when parent has no options
          setLevelOptions((prevOptions) => ({
            ...prevOptions,
            [currentLevel]: [],
          }))
        }
      } else if (levelOptions[currentLevel]?.length > 0) {
        // Clear child options when parent is not selected
        setLevelOptions((prevOptions) => ({
          ...prevOptions,
          [currentLevel]: [],
        }))
      }
    }
  }, [currentValue, levels, levelOptions])

  return (
    <FormField
      control={control}
      name={formFieldName}
      render={({ field: formField }) => {
        // Ensure we have a valid value object
        const value = (formField.value as CascadingFieldValue) || ({} as CascadingFieldValue)

        // Update the current value if it's different from the form value
        // This is safe because it doesn't directly update state during render
        if (isMountedRef.current && JSON.stringify(value) !== JSON.stringify(currentValue)) {
          // Use requestAnimationFrame to defer the state update until after render
          requestAnimationFrame(() => {
            setCurrentValue(value)
          })
        }

        // Handle changes to a level
        const handleLevelChange = (levelKey: string, levelValue: string | string[]) => {
          const newValue = { ...value, [levelKey]: levelValue }

          // Clear all child levels
          const levelIndex = levels.indexOf(levelKey)
          if (levelIndex >= 0) {
            for (let i = levelIndex + 1; i < levels.length; i++) {
              const childLevel = levels[i]
              const isMultiSelect = field.isMultiSelect?.[childLevel as keyof typeof field.isMultiSelect]
              newValue[childLevel] = isMultiSelect ? [] : ''
            }
          }

          formField.onChange(newValue)
        }

        const fieldControl = (
          <div className={cn('flex flex-col space-y-2', variant === 'row' && '!mt-0 flex-row space-x-0 space-y-0')}>
            {/* Dynamically render comboboxes for each level */}
            {levels.map((levelKey: string, index: number) => {
              // Fix the shouldShow logic - it should be a boolean, not a string or empty value
              const shouldShow =
                index === 0 ||
                (!!value[levels[index - 1]] && levelOptions[levelKey] && levelOptions[levelKey].length > 0)

              const levelValue = value[levelKey]
              const isMultiSelect = field.isMultiSelect?.[levelKey as keyof typeof field.isMultiSelect]
              const levelLabel = fieldLabels[levelKey as keyof typeof fieldLabels] || `Level ${index + 1}`
              const options = levelOptions[levelKey] || []
              const comboboxOptions = options.map((option) => ({
                label: option.label,
                value: option.value,
              }))

              if (!shouldShow) {
                return null // Don't render this level if it shouldn't be shown
              }

              // Force a key change when options change to ensure the component re-renders
              const optionsKey = JSON.stringify(comboboxOptions)
              const componentKey = `${levelKey}-${optionsKey}`
              const nextLevelKey = levels[index + 1]
              const showRight =
                variant === 'row' &&
                index < levels.length - 1 &&
                !!value[levelKey] &&
                !!nextLevelKey &&
                (levelOptions[nextLevelKey]?.length || 0) > 0
              const showLeft = variant === 'row' && index > 0

              // --- AMENDMENT: Render first level as cards if needed ---
              if (index === 0 && variant === 'column' && isCascadingCardOptionArray(options)) {
                return (
                  <div key={componentKey} className="flex-1">
                    <CardSelector
                      options={options}
                      value={typeof levelValue === 'string' ? levelValue : ''}
                      onChange={(newValue) => handleLevelChange(levelKey, newValue)}
                      disabled={!!field.readOnly}
                    />
                  </div>
                )
              }
              // --- END AMENDMENT ---

              return (
                <div key={componentKey} className={cn('flex-1', variant === 'row' && index > 0 && '-ml-3')}>
                  {isMultiSelect ? (
                    <MultiLevelCombobox
                      options={comboboxOptions}
                      value={Array.isArray(levelValue) ? levelValue : []}
                      onChange={(newValue) => handleLevelChange(levelKey, newValue)}
                      placeholder={`Select ${levelLabel.toLowerCase()}`}
                      disabled={!shouldShow || !!field.readOnly}
                      variant={variant}
                      showLeftNotch={showLeft}
                      showRightNotch={showRight}
                    />
                  ) : (
                    <SingleLevelCombobox
                      options={comboboxOptions}
                      value={typeof levelValue === 'string' ? levelValue : ''}
                      onChange={(newValue) => handleLevelChange(levelKey, newValue)}
                      placeholder={`Select ${levelLabel.toLowerCase()}`}
                      disabled={!shouldShow || !!field.readOnly}
                      variant={variant}
                      showLeftNotch={showLeft}
                      showRightNotch={showRight}
                    />
                  )}
                </div>
              )
            })}
          </div>
        )

        return (
          <FormItem className="flex w-full flex-col space-y-2">
            <div className="flex justify-start gap-2">
              <FormLabel className={variant === 'row' ? 'sr-only' : 'block'}>{field.title}</FormLabel>
              {variant !== 'row' && <DocumentationButton documentation={field.documentation} />}
            </div>
            {variant === 'column' && <FormDescription className="text-xs">{field.description}</FormDescription>}

            {variant === 'column' ? (
              fieldControl
            ) : (
              <div className={'!mt-0 flex items-center gap-x-1 text-muted-foreground'}>
                <TooltipButton tooltip={field.title}>
                  <div className="shrink-0">
                    <IconInformationOutline className="size-4" />
                  </div>
                </TooltipButton>
                {fieldControl}
              </div>
            )}

            <FormMessage />
          </FormItem>
        )
      }}
    />
  )
}
